// Turning what a browser records into something Asterisk will play.
//
// Kevin, 2026-08-16: browser recording AND upload, for busy / unavailable / temp.
//
// THE PROBLEM. `MediaRecorder` gives WebM/Opus. Asterisk plays a mailbox greeting
// as WAV, 8 kHz, mono, 16-bit PCM. Nothing in between converts it: a Cloudflare
// Worker has no ffmpeg, and audio transcoding is not something to attempt in a
// request path. So the browser — which already has a full audio pipeline in
// `AudioContext` — does it, and every copy from that point on (R2, the agent, the
// mailbox) is the same bytes.
//
// WHY 8 kHz MONO AND NOT "GOOD QUALITY". It is a telephone. The call itself is
// 8 kHz narrowband, so a 44.1 kHz stereo greeting is resampled by Asterisk anyway —
// and resampled badly, at call time, on the PBX. Doing it here means it is done once,
// predictably, and what we store is exactly what a caller hears.
//
// A NOTE ON `OfflineAudioContext` AND SAMPLE RATES. Some browsers refuse to
// construct one below 8000 Hz and some older ones refused 8000 itself. 8000 is
// within spec and works in current Chrome/Firefox/Safari; if `encodeWavFromBlob`
// throws on construction, that is the case to catch — not something to paper over
// by silently shipping 44.1 kHz, which would produce a file the agent must reject.

/** Asterisk's mailbox format. Changing these means changing what callers hear. */
export const TARGET_SAMPLE_RATE = 8000;
export const TARGET_CHANNELS = 1;

/**
 * Mix to mono and resample, using the browser's own audio pipeline.
 * @returns {Promise<Float32Array>} samples at TARGET_SAMPLE_RATE
 */
async function toMono8k(audioBuffer) {
  const frames = Math.max(
    1,
    Math.round(audioBuffer.duration * TARGET_SAMPLE_RATE),
  );
  const offline = new OfflineAudioContext(TARGET_CHANNELS, frames, TARGET_SAMPLE_RATE);
  const src = offline.createBufferSource();
  src.buffer = audioBuffer;
  // Connecting a multi-channel source to a 1-channel destination mixes it down —
  // the spec's default up/down-mix. Taking channel 0 instead would silently drop
  // half of a stereo recording, which on a laptop with two mics is half the voice.
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

/** Float samples in [-1, 1] → 16-bit little-endian PCM. */
function toPcm16(samples) {
  const out = new DataView(new ArrayBuffer(samples.length * 2));
  for (let i = 0; i < samples.length; i++) {
    // CLAMP FIRST. Float32 from the mixer can exceed ±1 after summing channels, and
    // wrapping instead of clamping turns a loud syllable into a burst of noise.
    const s = Math.max(-1, Math.min(1, samples[i]));
    out.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return out;
}

/** A 44-byte canonical RIFF/WAVE header for 16-bit PCM. */
function wavHeader(dataBytes, { sampleRate = TARGET_SAMPLE_RATE, channels = TARGET_CHANNELS } = {}) {
  const buf = new ArrayBuffer(44);
  const v = new DataView(buf);
  const ascii = (off, str) => { for (let i = 0; i < str.length; i++) v.setUint8(off + i, str.charCodeAt(i)); };
  const bytesPerSample = 2;
  ascii(0, 'RIFF');
  v.setUint32(4, 36 + dataBytes, true);     // file size - 8
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  v.setUint32(16, 16, true);                // PCM fmt chunk size
  v.setUint16(20, 1, true);                 // format 1 = PCM
  v.setUint16(22, channels, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * channels * bytesPerSample, true); // byte rate
  v.setUint16(32, channels * bytesPerSample, true);              // block align
  v.setUint16(34, 8 * bytesPerSample, true);                     // bits per sample
  ascii(36, 'data');
  v.setUint32(40, dataBytes, true);
  return buf;
}

/**
 * Any audio the browser can decode → a WAV Blob Asterisk will play.
 *
 * Handles both inputs this feature accepts: a `MediaRecorder` blob (WebM/Opus) and
 * an uploaded file (WAV/MP3/M4A — whatever `decodeAudioData` supports). Both come
 * out identical, so nothing downstream has to care which it was.
 *
 * @throws if the audio cannot be decoded — an upload of something that is not audio
 *         must fail here, loudly, rather than reach a mailbox.
 */
export async function encodeWavFromBlob(blob) {
  const bytes = await blob.arrayBuffer();
  // A plain AudioContext for decoding: it accepts any sample rate the file uses,
  // where an OfflineAudioContext fixed at 8 kHz would refuse some inputs outright.
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(bytes.slice(0));
  } catch {
    throw new Error('That file does not look like audio we can read. Try a WAV or MP3, or record it here instead.');
  } finally {
    // Chrome caps how many AudioContexts a page may hold; leaking one per attempt
    // makes the fourth or fifth recording fail for no visible reason.
    ctx.close?.();
  }

  const samples = await toMono8k(decoded);
  const pcm = toPcm16(samples);
  const header = wavHeader(pcm.byteLength);
  return new Blob([header, pcm.buffer], { type: 'audio/wav' });
}

/** Seconds of audio in an encoded WAV, read from its own header. */
export function wavDuration(arrayBuffer) {
  const v = new DataView(arrayBuffer);
  const byteRate = v.getUint32(28, true);
  const dataBytes = v.getUint32(40, true);
  return byteRate > 0 ? dataBytes / byteRate : 0;
}

/**
 * Is this really the format the mailbox needs?
 *
 * Used before upload so a browser quirk is caught here rather than by the agent's
 * 415 — and so the failure names the actual problem instead of "the PBX refused it".
 */
export function inspectWav(arrayBuffer) {
  if (arrayBuffer.byteLength < 44) return { ok: false, why: 'too short to be a WAV file' };
  const v = new DataView(arrayBuffer);
  const tag = (off) => String.fromCharCode(v.getUint8(off), v.getUint8(off + 1), v.getUint8(off + 2), v.getUint8(off + 3));
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return { ok: false, why: 'not a RIFF/WAVE file' };
  const format = v.getUint16(20, true);
  const channels = v.getUint16(22, true);
  const sampleRate = v.getUint32(24, true);
  const bits = v.getUint16(34, true);
  if (format !== 1) return { ok: false, why: `compressed WAV (format ${format}), not PCM` };
  if (channels !== TARGET_CHANNELS) return { ok: false, why: `${channels} channels, not mono` };
  if (sampleRate !== TARGET_SAMPLE_RATE) return { ok: false, why: `${sampleRate} Hz, not ${TARGET_SAMPLE_RATE} Hz` };
  if (bits !== 16) return { ok: false, why: `${bits}-bit, not 16-bit` };
  return { ok: true, sampleRate, channels, bits, duration: wavDuration(arrayBuffer) };
}
